import TableWithPagination from "@components/Tables/TableWithPagination";
import TableHeaderControls from "@components/Project/TableHeaderControls";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import { Badge, ConfirmDeleteModal } from "@components/UI";
import queryClient from "@configs/react-query/query-client";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { formatDateTime, uppercase, formatDateForRequest, isNotNullAndEmptyArray } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { refreshPaginatedListAfterDelete } from "@utils/pagination";
import { useEffect, useRef, useState, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRoutePermissions } from "@contexts/RoutePermissionContext";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import { LOCAL_STORAGE } from "@constants/index";
import { PermissionContext } from "@contexts/PermissionContext";

export default function ProductList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasAddPermission, hasDeletePermission } = useRoutePermissions();
  const { hasManagePermission } = useContext(PermissionContext);
  const { colors } = useTheme();
  const { creatorList, fetchSearchInfo } = useSearchInfo();
  const currentUserId = localStorage.getItem(LOCAL_STORAGE.USER_ID);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchFilters, setSearchFilters] = useState({});
  const [productList, setProductList] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.PRODUCT_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const isInitialMount = useRef(true);
  const prevSearchFilters = useRef({});
  const prevCurrentPage = useRef(1);

  // Filter users with PRODUCT_MANAGER permission
  const productManagerList = creatorList.filter(user =>
    user.permissions && user.permissions.includes("PRODUCT_MANAGER")
  );

  // Advanced Search Fields Configuration
  const advancedSearchFields = [
    {
      name: "productManager",
      label: "Product Manager",
      type: "select",
      placeholder: t("product.product-manager"),
      options: productManagerList,
      displayFormatLabelWithID: true,
    },
    {
      name: "createdAtFrom",
      placeholder: t("common.create-at-from"),
      type: "date",
    },
    {
      name: "createdAtTo",
      placeholder: t("common.create-at-to"),
      type: "date",
      minDate: "createdAtFrom",
    },    
    {
      name: "createrId",
      label: "Creator",
      type: "select",
      placeholder: t("system.creator"),
      options: creatorList,
      displayFormatLabelWithID: true,
    },
  ];

  const { showAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: advancedSearchFields,
    quickSearchField: "productName",
    setSearchCriteria: setSearchFilters,
  });

  // Single initial fetch + refetch when advanced search panel opens/closes
  useEffect(() => {
    fetchData();
  }, [showAdvancedSearch]);

  useEffect(() => {
    fetchSearchInfo();
  }, []);

  useEffect(() => {
    if (!isInitialMount.current && prevCurrentPage.current !== currentPage) {
      fetchData();
      prevCurrentPage.current = currentPage;
    }
  }, [currentPage]);

  // Must run before searchFilters effect clears isInitialMount on mount
  useEffect(() => {
    if (!isInitialMount.current) {
      fetchData();
    }
  }, [itemsPerPage]);

  useEffect(() => {
    if (!isInitialMount.current) {
      const currentFilters = JSON.stringify(searchFilters);
      const prevFilters = JSON.stringify(prevSearchFilters.current);

      if (currentFilters !== prevFilters) {
        if (currentPage !== 1) {
          setCurrentPage(1);
        } else {
          fetchData();
        }
        prevSearchFilters.current = searchFilters;
      }
    } else {
      isInitialMount.current = false;
      prevSearchFilters.current = searchFilters;
      prevCurrentPage.current = currentPage;
    }
  }, [searchFilters]);

  // Helper function to get user name by ID
  const getUserNameById = (userId) => {
    if (!userId) return "";
    const user = creatorList.find(user => user.userId === userId);
    return user ? user.userName : userId; // Return name if found, otherwise return ID
  };

  const isDefaultProduct = (row) => row?.defaultFlag === "Y";

  const columns = ["productName", "productManager", "createrName", "createdAt", "updatedAt", "updaterName"];

  const columnConfig = {
    productName: {
      truncate: false,
      style: {
        minWidth: "200px",
        overflow: "hidden",
      },
      headerRender: () => t("product.product-name"),
      customRender: (value, row) => (
        <div className="flex w-full min-w-0 items-center gap-2 overflow-hidden">
          <span className="min-w-0 flex-1 truncate" title={value || undefined}>
            {value}
          </span>
          {isDefaultProduct(row) && (
            <Badge variant="primary" size="xs" className="shrink-0" data-testid="product-default-badge">
              {uppercase(t("product.default"))}
            </Badge>
          )}
        </div>
      ),
    },
    productManager: {
      style: {
        minWidth: "100px",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("product.product-manager"),
      customRender: (value, row) => (
        <UserNameWithId name={getUserNameById(row.productManager)} id={row.productManager} />
      ),
    },
    createrName: {
      style: {
        minWidth: "100px",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("system.creator"),
      customRender: (value, row) => <UserNameWithId name={row.createrName} id={row.createrId} />,
    },
    createdAt: {
      style: {
        minWidth: "150px",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("common.create-at"),
      customRender: (value, row) => formatDateTime(row.createdAt),
    },
    updatedAt: {
      style: {
        minWidth: "150px",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("common.last-modified-date"),
      customRender: (value, row) => formatDateTime(row.updatedAt),
    },
    updaterName: {
      style: {
        minWidth: "150px",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("common.last-modified-by"),
      customRender: (value, row) => <UserNameWithId name={row.updaterName} id={row.updaterId} />,
    },
  };

  const formatProductDeleteConflictNames = (message) => {
    if (Array.isArray(message)) {
      return message.filter(Boolean).join(", ");
    }
    return message || "";
  };

  const deleteData = useMutationData(
    "products",
    {
      method: "DELETE",
    },
    {
      onSuccess: () => {
        refreshPaginatedListAfterDelete({
          currentPage,
          setCurrentPage,
          itemsPerPage,
          totalData,
          deletedCount: selectedRows.length,
          fetchData,
        });
        queryClient.invalidateQueries(queryKeys.getProducts);
        setSelectedRows([]);
        showSuccessToast(t("system.delete-product-successfully"));
        setShowDeleteModal(false);
        setDeleteLoading(false);
      },
      onError: (error) => {
        const deletedSeqs = error.response?.data?.deletedSeqs;

        if (isNotNullAndEmptyArray(deletedSeqs)) {
          showSuccessToast(`${deletedSeqs.length} ${t("system.delete-product-successfully")}`);
          setSelectedRows([]);
          refreshPaginatedListAfterDelete({
            currentPage,
            setCurrentPage,
            itemsPerPage,
            totalData,
            deletedCount: deletedSeqs.length,
            fetchData,
          });
          queryClient.invalidateQueries(queryKeys.getProducts);
        }

        const conflictProductNames = formatProductDeleteConflictNames(error.response?.data?.message);
        if (error.response?.status === 409 && conflictProductNames) {
          showErrorToast(
            t("status.error"),
            t("system.delete-product-error-conflict", { productNames: conflictProductNames }),
          );
        } else if (!isNotNullAndEmptyArray(deletedSeqs)) {
          showErrorToast(t("system.delete-product-failed"));
        }

        console.error("Error during delete data:", error);
        setShowDeleteModal(false);
        setDeleteLoading(false);
      },
    },
  );

  const handleConfirmDelete = () => {
    setDeleteLoading(true);
    deleteData.mutate(selectedRows);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleCheckboxChange = (newSelectedRows) => {
    setSelectedRows([...newSelectedRows]);
  };

  const canDeleteProduct = (row) => {
    if (hasManagePermission) return true;
    if (!row?.createrId) return false;
    if (!currentUserId) return false;
    return row.createrId === currentUserId;
  };

  const onRowClick = (rowData) => {
    navigate(`./${rowData.productSeq}`);
  };

  const fetchData = () => {
    const effectiveCriteria = resolveSearchCriteria(searchFilters, {
      quickSearchField: "productName",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      const value = effectiveCriteria[key];
      if (value !== "" && value !== undefined) {
        // Format date fields using formatDateForRequest function
        if (key === "createdAtFrom" || key === "createdAtTo") {
          acc[key] = formatDateForRequest(value);
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});

    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPage - 1);
    getListData.mutate(bodyRequest);
  };

  const getListData = useMutationData(
    queryKeys.getListProducts,
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setProductList(response.object);
          setTotalData(response.objectCount);
          setTotalPages(Math.ceil(response.objectCount / itemsPerPage));
        }
      },
      onError: (error) => {
        console.error("Error during get data:", error);
        showErrorToast(t("system.get-product-list-error"));
      },
    },
  );

  // Search handlers
  const searchHandler = useCallback((inputData) => {
    setSearchFilters((prev) => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="product-list-page">
      {/* Header */}
      <div className={`${colors.textPlaceholder} py-1`}>
        <div className="flex items-center justify-between">
          <h1 className={`text-[28px] uppercase font-bold ${colors.textPrimary}`}>{t("product.product-management")}</h1>
        </div>
      </div>

      {/* Header with search and controls */}
      <div className={`shrink-0 pt-4 ${colors.bgSecondary}`}>
        <TableHeaderControls
          onAdvancedSearchToggle={handleAdvancedSearchToggle}
          isAdvancedPanelOpen={showAdvancedSearch}
          onDeleteProject={() => setShowDeleteModal(true)}
          onAdd={() => navigate("/products/new")}
          searchField="productName"
          searchValue={searchFilters.productName ?? ""}
          onSearch={searchHandler}
          hasSelectedRows={selectedRows.length > 0}
          placeholderSearch={t("common.search-placeholder")}
          titleAddButton={t("system.add-new-product")}
          titleDeleteButton={t("system.delete-product")}
          isDisplayAddButton={hasAddPermission}
          isDisplayDeleteButton={hasDeletePermission}
          isSearching={getListData.isPending}
          searchInputTestId="product-list-search-input"
          addButtonTestId="product-list-add-button"
        />
      </div>

      {/* Advanced Search */}
      {showAdvancedSearch && (
        <AdvancedSearch
          title={t("common.advanced-search")}
          searchFields={advancedSearchFields}
          searchHandler={searchHandler}
          withoutSearch={true}
          gridCols="md:grid-cols-2 lg:grid-cols-3"
        />
      )}
      {/* Product Table */}
      <div className="flex-grow min-h-0" data-testid="product-list-table">
        <TableWithPagination
          columnConfig={columnConfig}
          data={productList}
          columns={columns}
          onRowClick={onRowClick}
          loading={getListData.isPending}
          totalPages={totalPages}
          totalData={totalData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          maxRowsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          selectedRows={selectedRows}
          onCheckboxChange={handleCheckboxChange}
          disabledRow={[]}
          hasCheckbox={hasDeletePermission}
          hasRadioButton={false}
          rowKey="productSeq"
          shouldShowGoToPage={true}
          isCheckboxDisabled={(row) => isDefaultProduct(row) || !canDeleteProduct(row)}
          showItemsPerPageSelector={true}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={uppercase(t("system.delete-product"))}
        description={t("system.delete-product-warning", { count: selectedRows.length })}
        loading={deleteLoading}
      />
    </div>
  );
}
